import {combineReducers} from 'redux';
import {formReducer, modelReducer, actions as formActions} from 'react-redux-form';

/**
 * Duck that wraps the RRF reducers and represents a form and model object in state.
 *
 * @param name the state key
 * @param duckEntity the entity managed by this duck
 * @param {Object} options - overrides default options
 * @param {Object} options.initialState - initial state for model
 * @param {Function} options.transformLoad - alter model when loading
 * @param {Function} options.transformSave - alter model before saving
 */
export default function (name, duckEntity, options = {}) {
  const {
    initialState,
    transformLoad = (model) => model,
    transformSave = (model) => model,
    modelKey = `${name}.model`
  } = options;

  const actionType = (action) => `schooladmin/${name}/${action}`;

  const types = {
    SAVE_BEGIN: actionType('SAVE_BEGIN'),
    SAVE_END: actionType('SAVE_END'),
  };

  const saving = (state = false, action) => {
    switch (action.type) {
      case types.SAVE_BEGIN:
        return true;
      case types.SAVE_END:
        return false;
      default:
        return state;
    }
  };

  /**
   * Loads a model into react-redux-form state.
   *
   * @param model the model to load
   */
  const load = (model) => (dispatch) => {
    dispatch(formActions.load(modelKey, transformLoad(model)));
  };

  /**
   * Saves a model.
   *
   * @param model the model to save
   */
  const save = (model) => (dispatch) => {
    dispatch({type: types.SAVE_BEGIN});

    return new Promise((resolve, reject) => {
      dispatch(duckEntity.actions.save(transformSave(model)))
        .then(response => duckEntity.actions.denormalizeObjects(response))
        .then(model => {
          dispatch(load(model));
          resolve(model);
        })
        .catch(response => {
          const model = response[duckEntity.config.jsonRoot];
          if (model) {
            dispatch(load(model));
          }
          reject(response);
        })
        .then(() => dispatch({type: types.SAVE_END}))
    });
  };

  this.actions = {
    types,
    load,
    save
  };

  this.name = name;
  this.model = `${name}.model`;
  this.form = `${name}.form`;
  this.reducer = combineReducers({
    model: modelReducer(modelKey, initialState),
    form: formReducer(modelKey),
    saving
  });
}
